<script setup>
/**
 * Pipeline Charts - Gráficos modernos do Pipeline
 * Usando LineChart moderno e FunnelChart visual
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import LineChart from 'dashboard/components-next/Charts/LineChart.vue';
import { DonutChart } from 'dashboard/components-next/Reports';

const props = defineProps({
  conversionFunnel: {
    type: Object,
    default: null,
  },
  stageDistribution: {
    type: Object,
    default: null,
  },
  timeInStages: {
    type: Object,
    default: null,
  },
});

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

// Cores
const colors = {
  blue: '#3b82f6',
  green: '#10b981',
  purple: '#8b5cf6',
  orange: '#f59e0b',
  cyan: '#06b6d4',
  pink: '#ec4899',
  red: '#ef4444',
};

// Helpers - defined before computed properties
const formatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Computed: Dados para gráfico de distribuição por etapa (Donut)
// DonutChart expects Array<{label, value, color}>
const distributionChartData = computed(() => {
  if (!props.stageDistribution?.distribution) {
    return [];
  }

  const distribution = props.stageDistribution.distribution;

  return distribution.map(d => ({
    label: d.label,
    value: d.value,
    color: d.color || colors.blue,
  }));
});

// Computed: Dados para gráfico de tempo por etapa (LineChart)
const timeChartData = computed(() => {
  if (!props.timeInStages?.stages) {
    return { labels: [], datasets: [] };
  }

  const stages = props.timeInStages.stages;

  return {
    labels: stages.map(s => s.label),
    datasets: [
      {
        label: t('KANBAN_REPORTS.CHARTS.AVERAGE_TIME'),
        data: stages.map(s => Math.round(s.average_time_seconds / 60)),
        borderColor: colors.purple,
        backgroundColor: `${colors.purple}15`,
        fill: true,
      },
    ],
  };
});

// Computed: Estágios do funil de conversão
const funnelStages = computed(() => {
  if (!props.conversionFunnel?.stages) {
    return [];
  }

  const stages = props.conversionFunnel.stages;
  const maxCount = Math.max(...stages.map(s => s.card_count), 1);

  return stages.map((stage, idx) => ({
    ...stage,
    widthPercent: Math.max((stage.card_count / maxCount) * 100, 30),
    isFirst: idx === 0,
    isLast: idx === stages.length - 1,
  }));
});
</script>

<template>
  <div class="space-y-6">
    <!-- Gráficos lado a lado -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Gráfico de Distribuição por Etapa (Donut) -->
      <div class="p-5 rounded-xl bg-n-solid-2 border border-n-weak">
        <div class="mb-4">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ $t('KANBAN_REPORTS.CHARTS.STAGE_DISTRIBUTION') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('KANBAN_REPORTS.CHARTS.STAGE_DISTRIBUTION_DESC') }}
          </p>
        </div>
        <div v-if="distributionChartData.length > 0" class="h-64">
          <DonutChart :data="distributionChartData" />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-n-slate-10"
        >
          {{ $t('KANBAN_REPORTS.CHARTS.NO_DATA') }}
        </div>
      </div>

      <!-- Gráfico de Tempo por Etapa (Line) -->
      <div class="p-5 rounded-xl bg-n-solid-2 border border-n-weak">
        <div class="mb-4">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ $t('KANBAN_REPORTS.CHARTS.TIME_IN_STAGES') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('KANBAN_REPORTS.CHARTS.TIME_IN_STAGES_DESC') }}
          </p>
        </div>
        <div v-if="timeChartData.labels.length > 0">
          <LineChart :data="timeChartData" height="240px" />
        </div>
        <div
          v-else
          class="flex items-center justify-center h-64 text-n-slate-10"
        >
          {{ $t('KANBAN_REPORTS.CHARTS.NO_DATA') }}
        </div>
      </div>
    </div>

    <!-- Funil de Conversão Visual -->
    <div class="p-5 rounded-xl bg-n-solid-2 border border-n-weak">
      <div class="mb-6">
        <h2 class="text-base font-medium text-n-slate-12">
          {{ $t('KANBAN_REPORTS.CHARTS.CONVERSION_FUNNEL') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('KANBAN_REPORTS.CHARTS.CONVERSION_FUNNEL_DESC') }}
        </p>
      </div>

      <div
        v-if="funnelStages.length > 0"
        class="flex flex-col items-center gap-3 py-4"
      >
        <div
          v-for="stage in funnelStages"
          :key="stage.name"
          class="w-full flex flex-col items-center"
        >
          <!-- Barra do Funil -->
          <div
            class="relative rounded-xl shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group"
            :style="{
              width: `${stage.widthPercent}%`,
              minWidth: '280px',
              backgroundColor: stage.color || colors.blue,
            }"
          >
            <div class="px-6 py-4 text-white">
              <!-- Nome e métricas -->
              <div class="flex items-center justify-between flex-wrap gap-4">
                <div class="font-semibold text-lg">
                  {{ stage.display_name || stage.name }}
                </div>
                <div class="flex items-center gap-6 text-sm flex-wrap">
                  <div class="flex items-center gap-2">
                    <span class="opacity-80">
                      {{ $t('KANBAN_REPORTS.CHARTS.CARDS') }}
                    </span>
                    <span class="font-bold">{{ stage.card_count }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="opacity-80">
                      {{ $t('KANBAN_REPORTS.CHARTS.VALUE') }}
                    </span>
                    <span class="font-bold">
                      {{ formatCurrency(stage.total_value) }}
                    </span>
                  </div>
                  <div
                    v-if="!stage.isFirst && stage.conversion_rate !== undefined"
                    class="flex items-center gap-2"
                  >
                    <span class="opacity-80">
                      {{ $t('KANBAN_REPORTS.CHARTS.CONVERSION') }}
                    </span>
                    <span class="font-bold">{{ stage.conversion_rate }}%</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Indicador de perda -->
            <div
              v-if="!stage.isLast && stage.drop_off_rate"
              class="absolute -bottom-5 left-1/2 transform -translate-x-1/2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <span class="i-ph-arrow-down mr-1" />
              {{ stage.drop_off_rate }}%
            </div>
          </div>

          <!-- Seta conectora -->
          <div
            v-if="!stage.isLast"
            class="flex items-center justify-center h-6"
          >
            <span class="i-ph-caret-down text-xl text-n-slate-9" />
          </div>
        </div>
      </div>

      <div v-else class="flex items-center justify-center h-40 text-n-slate-10">
        {{ $t('KANBAN_REPORTS.CHARTS.NO_DATA') }}
      </div>
    </div>
  </div>
</template>
